import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { api } from "@/lib/auth";
import { CATEGORIES, eur } from "@/lib/constants";

const MAIN_NEEDS = [
  {
    n: "A",
    tag: "Je veux créer un support",
    title: "Charbonier Studio crée à partir de votre idée.",
    body: "Vous avez une idée, un thème ou un besoin ? Charbonier Studio réalise le visuel et prépare le fichier final.",
    examples: ["Carte", "Flyer", "Faire-part", "Marque-page", "Affiche", "Réglette", "Support pédagogique"],
    cta: "Créer mon support",
    to: "/creer-mon-support",
  },
  {
    n: "B",
    tag: "J'ai déjà mon projet",
    title: "Charbonier Studio prépare votre fichier existant.",
    body: "Votre visuel existe déjà ? Charbonier Studio le contrôle, l'adapte et le prépare pour l'impression — sans le réinventer.",
    examples: ["Mise au format", "Planche PDF", "Correction imprimeur", "Contrôle prépresse"],
    cta: "Préparer mon projet",
    to: "/preparer-mon-projet",
  },
];

const SECONDARY = [
  { title: "Mise en page & édition", body: "Livre, livret, brochure, magazine, revue.", to: "/mise-en-page" },
  { title: "Couvertures de livres", body: "Première seule ou couverture complète.", to: "/services/couvertures" },
  { title: "SOS Print · urgence", body: "Votre imprimeur refuse votre fichier ?", to: "/sos-print" },
  { title: "Projet sur mesure", body: "Décrivez votre besoin, on revient vers vous.", to: "/demande-devis" },
];

const TRUST = [
  { title: "Contrôle humain", body: "Chaque commande est vérifiée avant livraison." },
  { title: "Respect des spécifications", body: "Dimensions, résolution, fonds perdus et paramètres techniques sont contrôlés selon votre projet." },
  { title: "Interlocuteur direct", body: "Vous échangez avec la personne qui réalise votre travail." },
  { title: "Aucun supplément sans accord", body: "Si votre demande dépasse la prestation commandée, vous êtes prévenu avant toute intervention supplémentaire." },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  useEffect(() => { api.get("/services").then((r) => setFeatured(r.data.slice(0, 6))); }, []);

  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="cs-cream border-b border-black/10">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 pt-16 lg:pt-24 pb-20 lg:pb-32">
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-10">
              <div className="cs-eyebrow text-neutral-500">Atelier graphique · Print & édition</div>
              <h1 className="cs-h1 mt-6 text-[9vw] sm:text-[7vw] lg:text-[5.6rem] xl:text-[6.4rem]">
                Chaque fichier,<br />
                <span className="italic font-light text-[#0E7C90]">prêt pour la presse.</span>
              </h1>
              <p className="mt-8 font-serif text-2xl lg:text-3xl max-w-2xl leading-snug">
                L'atelier qui crée, met en page et prépare vos documents<br className="hidden md:block" /> pour l'imprimeur — au bon format, à la bonne colorimétrie, du premier coup.
              </p>
              <p className="mt-6 cs-body max-w-xl text-neutral-700 text-[15px] sm:text-base">
                Votre projet mérite mieux qu'un fichier approximatif. Charbonier Studio crée, adapte, contrôle
                et prépare vos documents pour le print et le numérique.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/services" className="cs-btn-primary" data-testid="hero-primary-cta">
                  Découvrir les prestations <ArrowUpRight size={18} />
                </Link>
                <Link to="/sos-print" className="cs-btn-ghost !text-black !border-black" data-testid="hero-secondary-cta">
                  Mon imprimeur refuse mon fichier
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Que souhaitez-vous faire — 3 grandes portes d'entrée */}
      <section className="cs-cream py-20 lg:py-28 border-b border-black/10" data-testid="section-needs">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <div className="cs-eyebrow text-neutral-500">Section — 01</div>
              <h2 className="cs-h2 mt-3 text-5xl lg:text-6xl">Que souhaitez-vous faire ?</h2>
            </div>
            <p className="max-w-md text-neutral-700 cs-body text-[15px] sm:text-base">
              Trois grandes portes d'entrée pour identifier votre besoin en quelques secondes.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {MAIN_NEEDS.map((n) => (
              <Link key={n.n} to={n.to} data-testid={`main-need-${n.n}`}
                className="group cs-card p-8 lg:p-10 flex flex-col justify-between min-h-[420px]">
                <div>
                  <div className="cs-eyebrow text-[#0E7C90]">Parcours {n.n} · {n.tag}</div>
                  <h3 className="cs-h3 mt-4 text-3xl lg:text-4xl">{n.title}</h3>
                  <p className="mt-4 text-neutral-700 leading-relaxed text-[15px] sm:text-base">{n.body}</p>
                  <div className="mt-6 flex flex-wrap gap-2">
                    {n.examples.map((e) => (
                      <span key={e} className="text-[12px] font-mono uppercase tracking-[0.14em] border border-black/15 px-2.5 py-1">{e}</span>
                    ))}
                  </div>
                </div>
                <div className="mt-8 pt-6 border-t border-black/10 flex items-center justify-between">
                  <span className="font-mono text-[13px] uppercase tracking-[0.14em] text-black group-hover:text-[#0E7C90] transition-colors">
                    {n.cta}
                  </span>
                  <ArrowUpRight size={20} className="text-black group-hover:text-[#0E7C90] transition-colors" />
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {SECONDARY.map((s, i) => (
              <Link key={i} to={s.to} data-testid={`secondary-need-${i}`}
                className="group flex flex-col justify-between border border-black/15 bg-white/50 hover:bg-black hover:text-[#FAF8F5] transition-colors px-5 py-5 min-h-[110px]">
                <span className="font-serif text-lg leading-tight">{s.title}</span>
                <div className="mt-3 flex items-center justify-between text-[12px] font-mono uppercase tracking-[0.14em]">
                  <span className="opacity-70">{s.body}</span>
                  <ArrowUpRight size={16} />
                </div>
              </Link>
            ))}
          </div>

          {/* Impression & livraison — encart discret */}
          <div className="mt-10 border border-black/15 bg-white/60 px-6 py-5 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6" data-testid="home-print-option">
            <div className="flex-1">
              <div className="cs-eyebrow text-[#0E7C90]">Impression &amp; livraison</div>
              <p className="mt-2 font-serif text-lg leading-snug">Vous souhaitez également recevoir vos supports imprimés&nbsp;?</p>
              <p className="mt-1 text-[13px] text-neutral-600 leading-relaxed">
                Charbonier Studio peut prendre en charge l'impression de votre création et sa livraison, une fois le fichier final validé.
              </p>
            </div>
            <Link to="/impression-livraison" data-testid="home-print-cta"
              className="cs-btn-ghost !text-black !border-black inline-flex items-center gap-2 self-start sm:self-auto">
              En savoir plus <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* SOS PRINT — bien avant le catalogue */}
      <section className="cs-dark cs-grain relative overflow-hidden" data-testid="section-sos">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-20 lg:py-28 grid lg:grid-cols-12 gap-12 items-center relative">
          <div className="lg:col-span-7">
            <div className="cs-eyebrow text-[#0E7C90]">SOS Print</div>
            <h2 className="cs-h2 mt-4 text-5xl lg:text-7xl text-[#FAF8F5]">
              Votre imprimeur<br />
              refuse votre fichier <span className="text-[#0E7C90]">?</span>
            </h2>
            <p className="mt-8 text-neutral-300 max-w-xl leading-relaxed text-[15px] sm:text-base">
              Fond perdu manquant ? Mauvais format ? PDF refusé ? CMJN ? Résolution insuffisante ?
              Vous ne comprenez pas les consignes de votre imprimeur ?
            </p>
            <p className="mt-4 text-neutral-300 max-w-xl leading-relaxed text-[15px] sm:text-base">
              Envoyez votre fichier ainsi que les indications reçues. Charbonier Studio s'occupe du reste.
            </p>
            <div className="mt-10">
              <Link to="/sos-print" className="cs-btn-primary" data-testid="sos-section-cta">
                Résoudre mon problème d'impression <ArrowUpRight size={18} />
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="border border-white/15 p-8 bg-[#1a1a1a]/60">
              <div className="cs-eyebrow text-neutral-500">Une seule prestation</div>
              <div className="mt-6">
                <div className="text-sm text-neutral-400">Rendre mon fichier conforme à l'imprimeur</div>
                <div className="mt-2 font-serif text-4xl text-[#FAF8F5]">29 €</div>
                <p className="mt-3 text-[13px] text-neutral-500 leading-relaxed">
                  Consignes techniques, fichier refusé ou problème signalé — un tarif unique.
                  Fonds perdus et préparation technique inclus.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Confiance — Un fichier réellement prêt */}
      <section className="cs-cream py-20 lg:py-24 border-b border-black/10" data-testid="section-trust">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="cs-eyebrow text-neutral-500">Section — 02</div>
          <h2 className="cs-h2 mt-3 text-4xl lg:text-6xl max-w-3xl">Un fichier réellement prêt à être utilisé.</h2>

          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {TRUST.map((t, i) => (
              <div key={i} className="border-t border-black/25 pt-6" data-testid={`trust-${i}`}>
                <div className="font-mono text-[11px] tracking-[0.24em] text-[#0E7C90]">0{i + 1}</div>
                <h3 className="cs-h3 mt-3 text-xl lg:text-2xl">{t.title}</h3>
                <p className="mt-3 text-[15px] text-neutral-700 leading-relaxed">{t.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Prestations aperçu — secondaire */}
      <section className="cs-cream py-20 lg:py-24 border-b border-black/10">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
            <div>
              <div className="cs-eyebrow text-neutral-500">Section — 03</div>
              <h2 className="cs-h2 mt-3 text-4xl lg:text-5xl">Quelques prestations</h2>
            </div>
            <Link to="/services" className="text-sm font-mono uppercase tracking-[0.14em] hover:text-[#0E7C90] transition-colors">
              Voir toutes les prestations →
            </Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.map((s) => (
              <Link key={s.id} to={`/prestation/${s.slug}`} className="cs-card p-8 flex flex-col justify-between min-h-[280px]"
                data-testid={`featured-service-${s.slug}`}>
                <div>
                  <div className="cs-eyebrow text-neutral-500">{CATEGORIES.find((c) => c.slug === s.category)?.short || s.category}</div>
                  <h3 className="cs-h3 mt-3 text-2xl">{s.name}</h3>
                  <p className="mt-3 text-[15px] text-neutral-600 line-clamp-3">{s.short_description}</p>
                </div>
                <div className="mt-6 pt-4 border-t border-black/10 flex items-end justify-between">
                  <div>
                    <div className="text-[11px] font-mono tracking-[0.14em] uppercase text-neutral-500">À partir de</div>
                    <div className="font-serif text-3xl">{eur(s.starting_price)}</div>
                  </div>
                  <ArrowUpRight size={20} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* À propos snippet */}
      <section className="cs-cream py-20 lg:py-24">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="cs-eyebrow text-neutral-500">Section — 04</div>
            <h2 className="cs-h2 mt-3 text-5xl lg:text-6xl">Un atelier graphique,<br />pas une plateforme.</h2>
            <p className="mt-8 text-neutral-700 cs-body max-w-xl text-[15px] sm:text-base">
              Charbonier Studio est un studio indépendant spécialisé en PAO, mise en page, print et
              préparation imprimeur. Un interlocuteur direct, une exécution précise et des fichiers qui passent en impression.
            </p>
            <div className="mt-8 flex gap-4 flex-wrap">
              <Link to="/a-propos" className="cs-btn-ghost !text-black !border-black">Découvrir le studio</Link>
              <Link to="/demande-devis" className="cs-btn-primary">Demander un devis <ArrowUpRight size={18} /></Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="border border-black/15 aspect-[4/5] bg-[#0A0A0A] flex items-center justify-center">
              <img src="/logo.webp" alt="Charbonier Studio" className="w-3/4 object-contain" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
